/**
 * IfcMapConversion as applied to an IFC layer's world coordinates (METRES),
 * stored as `layer.metadata.georef` (spec 2026-10-14-ifc-layers-2d-map-design.md
 * §2.2). Twin of `scripts/ifc_georef.py::apply_georef_xyz` and
 * `app/src/lib/ifcGeoref.ts::georefMatrix`; all pinned by
 * `scripts/test_fixtures/ifc/georef_cases.json`.
 */
export interface IfcGeoref {
  version: 1;
  source: 'IfcMapConversion' | 'IfcMapConversionScaled' | 'ePSet_MapConversion' | 'IfcRigidOperation';
  eastings: number;
  northings: number;
  height: number;
  /** atan2(XAxisOrdinate, XAxisAbscissa). */
  rotationRad: number;
  /** Effective scales on METRE coordinates (Scale / unit scale, × FactorX/Y/Z). */
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  crsName: string | null;
  raw: {
    scale: number;
    factorX: number;
    factorY: number;
    factorZ: number;
    xAxisAbscissa: number;
    xAxisOrdinate: number;
    unitScale: number;
    note?: string;
  };
}

export function applyIfcGeoref3(g: IfcGeoref, p: readonly [number, number, number]): [number, number, number] {
  const c = Math.cos(g.rotationRad);
  const s = Math.sin(g.rotationRad);
  const [x, y, z] = p;
  return [
    g.eastings + g.scaleX * c * x - g.scaleY * s * y,
    g.northings + g.scaleX * s * x + g.scaleY * c * y,
    g.height + g.scaleZ * z,
  ];
}

export function applyIfcGeorefXY(g: IfcGeoref, x: number, y: number): [number, number] {
  const [e, n] = applyIfcGeoref3(g, [x, y, 0]);
  return [e, n];
}

/** The layer's georef record, or null when absent / a version this code does not know. */
export function georefOf(metadata: unknown): IfcGeoref | null {
  const g = (metadata as { georef?: IfcGeoref } | null | undefined)?.georef;
  return g && g.version === 1 ? g : null;
}
